// TODO - remove lint disable when editing this file
/* eslint-disable */
/**
 * Override insertFragmentIntoContentState in draft.js to fix block data issue:
 * 1) If fragment size is 1 and target block is not empty, don't override block data.
 * 2) If fragment size > 1 and target block is not empty, don't override the first block.
 * i.e. The block data of the target block is preserved if it's not empty (text length > 0).
 *
 * And prevent duplicate entity keys are used for inserting blocks.
 */
const { List } = require('immutable')
const invariant = require('invariant')
const BlockMapBuilder = require('draft-js/lib/BlockMapBuilder')
const ContentBlockNode = require('draft-js/lib/ContentBlockNode')
const insertIntoList = require('draft-js/lib/insertIntoList')
const randomizeBlockMapKeys = require('draft-js/lib/randomizeBlockMapKeys')
const CharacterMetadata = require('draft-js/lib/CharacterMetadata')
const ArcUUID = require('~/util/arc-identifiers')

/**
 * @param {ContentState} contentState
 * @param {SelectionState} selectionState
 * @param {BlockMap} blockMap
 * @param {BlockNodeRecord} fragmentBlock
 * @param {string} targetKey
 * @param {number} targetOffset
 * @returns {ContentState}
 */
const updateExistingBlock = (
  contentState,
  selectionState,
  blockMap,
  fragmentBlock,
  targetKey,
  targetOffset
) => {
  const targetBlock = blockMap.get(targetKey)
  const text = targetBlock.getText()
  const chars = targetBlock.getCharacterList()
  const finalKey = targetKey
  const finalOffset = targetOffset + fragmentBlock.getText().length

  const newBlock = targetBlock.merge({
    text:
      text.slice(0, targetOffset) +
      fragmentBlock.getText() +
      text.slice(targetOffset),
    characterList: insertIntoList(
      chars,
      fragmentBlock.getCharacterList(),
      targetOffset
    ),
    // Apply same logic for block type like updateHead
    type: text ? targetBlock.getType() : fragmentBlock.getType(),
    data: text ? targetBlock.getData() : fragmentBlock.getData(),
  })

  return contentState.merge({
    blockMap: blockMap.set(targetKey, newBlock),
    selectionBefore: selectionState,
    selectionAfter: selectionState.merge({
      anchorKey: finalKey,
      anchorOffset: finalOffset,
      focusKey: finalKey,
      focusOffset: finalOffset,
      isBackward: false,
    }),
  })
}

/**
 * Appends text/characterList from the fragment first block to target block
 * @param {BlockNodeRecord} block
 * @param {number} targetOffset
 * @param {BlockMap} fragment
 * @returns {BlockNodeRecord}
 */
const updateHead = (block, targetOffset, fragment) => {
  const text = block.getText()
  const chars = block.getCharacterList()

  // Modify head portion of block.
  const headText = text.slice(0, targetOffset)
  const headCharacters = chars.slice(0, targetOffset)
  const appendToHead = fragment.first()

  return block.merge({
    text: headText + appendToHead.getText(),
    characterList: headCharacters.concat(appendToHead.getCharacterList()),
    type: headText ? block.getType() : appendToHead.getType(),
    data: headText ? block.getData() : appendToHead.getData(),
  })
}

/**
 * Appends offset text/characterList from the target block to the last
 * fragment block.
 * @param {BlockNodeRecord} block
 * @param {number} targetOffset
 * @param {BlockMap} fragment
 * @returns {BlockNodeRecord}
 */
const updateTail = (block, targetOffset, fragment) => {
  // Modify tail portion of block.
  const text = block.getText()
  const chars = block.getCharacterList()

  // Modify head portion of block.
  const blockSize = text.length
  const tailText = text.slice(targetOffset, blockSize)
  const tailCharacters = chars.slice(targetOffset, blockSize)
  const prependToTail = fragment.last()

  return prependToTail.merge({
    text: prependToTail.getText() + tailText,
    characterList: prependToTail.getCharacterList().concat(tailCharacters),
    data: prependToTail.getData(),
  })
}

/**
 * @param {ContentBlockNode} block
 * @param {BlockMap} blockMap
 * @returns {Array<string>}
 */
const getRootBlocks = (block, blockMap) => {
  const headKey = block.getKey()
  let rootBlock = block
  let rootBlocks = []

  // sometimes the fragment head block will not be part of the blockMap itself this can happen when
  // the fragment head is used to update the target block, however when this does not happen we need
  // to make sure that we include it on the rootBlocks since the first block of a fragment is always a
  // fragment root block
  if (blockMap.get(headKey)) {
    rootBlocks.push(headKey)
  }

  while (rootBlock && rootBlock.getNextSiblingKey()) {
    const lastSiblingKey = rootBlock.getNextSiblingKey()

    if (!lastSiblingKey) {
      break
    }

    rootBlocks.push(lastSiblingKey)
    rootBlock = blockMap.get(lastSiblingKey)
  }

  return rootBlocks
}

/**
 * @param {BlockMap} blockMap
 * @param {BlockMap} originalBlockMap
 * @param {ContentBlockNode} targetBlock
 * @param {ContentBlockNode} fragmentHeadBlock
 * @returns {BlockMap}
 */
const updateBlockMapLinks = (
  blockMap,
  originalBlockMap,
  targetBlock,
  fragmentHeadBlock
) => {
  return blockMap.withMutations((blockMapState) => {
    const targetKey = targetBlock.getKey()
    const headKey = fragmentHeadBlock.getKey()
    const targetNextKey = targetBlock.getNextSiblingKey()
    const targetParentKey = targetBlock.getParentKey()
    const fragmentRootBlocks = getRootBlocks(fragmentHeadBlock, blockMap)
    const lastRootFragmentBlockKey =
      fragmentRootBlocks[fragmentRootBlocks.length - 1]

    if (blockMapState.get(headKey)) {
      // update the fragment head when it is part of the blockMap otherwise
      blockMapState.setIn([targetKey, 'nextSibling'], headKey)
      blockMapState.setIn([headKey, 'prevSibling'], targetKey)
    } else {
      // update the target block that had the fragment head contents merged into it
      blockMapState.setIn(
        [targetKey, 'nextSibling'],
        fragmentHeadBlock.getNextSiblingKey()
      )
      blockMapState.setIn(
        [fragmentHeadBlock.getNextSiblingKey(), 'prevSibling'],
        targetKey
      )
    }

    // update the last root block fragment
    blockMapState.setIn(
      [lastRootFragmentBlockKey, 'nextSibling'],
      targetNextKey
    )

    // update the original target next block
    if (targetNextKey) {
      blockMapState.setIn(
        [targetNextKey, 'prevSibling'],
        lastRootFragmentBlockKey
      )
    }

    // update fragment parent links
    fragmentRootBlocks.forEach((blockKey) =>
      blockMapState.setIn([blockKey, 'parent'], targetParentKey)
    )

    // update targetBlock parent child links
    if (targetParentKey) {
      const targetParent = blockMap.get(targetParentKey)
      const originalTargetParentChildKeys = targetParent.getChildKeys()

      const targetBlockIndex = originalTargetParentChildKeys.indexOf(targetKey)
      const insertionIndex = targetBlockIndex + 1

      const newChildrenKeysArray = originalTargetParentChildKeys.toArray()

      // insert fragment children
      newChildrenKeysArray.splice(insertionIndex, 0, ...fragmentRootBlocks)

      blockMapState.setIn(
        [targetParentKey, 'children'],
        List(newChildrenKeysArray)
      )
    }
  })
}

/**
 * @param {ContentState} contentState
 * @param {SelectionState} selectionState
 * @param {BlockMap} blockMap
 * @param {BlockMap} fragment
 * @param {string} targetKey
 * @param {number} targetOffset
 * @returns {ContentState}
 */
const insertFragment = (
  contentState,
  selectionState,
  blockMap,
  fragment,
  targetKey,
  targetOffset
) => {
  const isTreeBasedBlockMap = blockMap.first() instanceof ContentBlockNode
  const newBlockArr = []
  const fragmentSize = fragment.size
  const target = blockMap.get(targetKey)
  const head = fragment.first()
  const tail = fragment.last()
  const finalOffset = tail.getLength()
  const finalKey = tail.getKey()
  const shouldNotUpdateFromFragmentBlock =
    isTreeBasedBlockMap &&
    (!target.getChildKeys().isEmpty() || !head.getChildKeys().isEmpty())

  blockMap.forEach((block, blockKey) => {
    if (blockKey !== targetKey) {
      newBlockArr.push(block)
      return
    }

    if (shouldNotUpdateFromFragmentBlock) {
      newBlockArr.push(block)
    } else {
      newBlockArr.push(updateHead(block, targetOffset, fragment))
    }

    // Insert fragment blocks after the head and before the tail.
    fragment
      // when we are updating the target block with the head fragment block we skip the first fragment
      // head since its contents have already been merged with the target block otherwise we include
      // the whole fragment
      .slice(shouldNotUpdateFromFragmentBlock ? 0 : 1, fragmentSize - 1)
      .forEach((fragmentBlock) => newBlockArr.push(fragmentBlock))

    // update tail
    newBlockArr.push(updateTail(block, targetOffset, fragment))
  })

  let updatedBlockMap = BlockMapBuilder.createFromArray(newBlockArr)

  if (isTreeBasedBlockMap) {
    updatedBlockMap = updateBlockMapLinks(
      updatedBlockMap,
      blockMap,
      target,
      head
    )
  }

  return contentState.merge({
    blockMap: updatedBlockMap,
    selectionBefore: selectionState,
    selectionAfter: selectionState.merge({
      anchorKey: finalKey,
      anchorOffset: finalOffset,
      focusKey: finalKey,
      focusOffset: finalOffset,
      isBackward: false,
    }),
  })
}

/**
 * @param {ContentState} contentState
 * @param {SelectionState} selectionState
 * @param {BlockMap} fragmentBlockMap
 * @returns {ContentState}
 */
const insertFragmentIntoContentState = (
  contentState,
  selectionState,
  fragmentBlockMap
) => {
  invariant(
    selectionState.isCollapsed(),
    '`insertFragment` should only be called with a collapsed selection state.'
  )

  const blockMap = contentState.getBlockMap()
  let fragment = randomizeBlockMapKeys(fragmentBlockMap)
  const targetKey = selectionState.getStartKey()
  const targetOffset = selectionState.getStartOffset()

  const targetBlock = blockMap.get(targetKey)

  if (targetBlock instanceof ContentBlockNode) {
    invariant(
      targetBlock.getChildKeys().isEmpty(),
      '`insertFragment` should not be called when a container node is selected.'
    )
  }

  // return contentState unchanged if fragment size is 0. This usually occurs when we copy
  // an empty string in certain editors, like VS Code, Google docs, and some browser elements
  if (!fragment.size){
    return contentState
  }

  // Prevent using duplicate entity keys for inserting blocks.
  // The drawback of this approach is all the entities created at intial load will get recreated,
  // but since we only use entity for links, the extra copy should be okay.
  // Ideally we should prevent using entities due to its limitations.
  fragment = fragment.map((blockToInsert) => {
    if (blockToInsert) {
      const entityRanges = []
      blockToInsert.findEntityRanges(
        (charMetaData) => !!charMetaData.getEntity(),
        (start, end) => {
          entityRanges.push({ start, end })
        }
      )
      entityRanges.forEach(({ start, end }) => {
        const entity = contentState.getEntity(blockToInsert.getEntityAt(start))
        contentState = contentState.createEntity(
          entity.getType(),
          entity.getMutability(),
          entity.getData()
        )
        const newEntityKey = contentState.getLastCreatedEntityKey()
        let characterList = blockToInsert.getCharacterList()
        for (let i = start; i < end; i++) {
          characterList = characterList.set(
            i,
            CharacterMetadata.applyEntity(characterList.get(i), newEntityKey)
          )
        }
        blockToInsert = blockToInsert.set('characterList', characterList)
      })
    }

    // ID generation when paragraph blocks are created, either by using content element menu, or by pasting paragraphs from outside the editor
    // this should only affect paragraphs
    if(!blockToInsert.getIn(["data", "id"]) && blockToInsert.getType() === 'unstyled'){
      const updatedBlockToInsert = blockToInsert.setIn(['data', 'id'], ArcUUID.create())
      return updatedBlockToInsert
    }
    return blockToInsert
  })

  // When we insert a fragment with a single block we simply update the target block
  // with the contents of the inserted fragment block
  if (fragment.size === 1) {
    return updateExistingBlock(
      contentState,
      selectionState,
      blockMap,
      fragment.first(),
      targetKey,
      targetOffset
    )
  }

  return insertFragment(
    contentState,
    selectionState,
    blockMap,
    fragment,
    targetKey,
    targetOffset
  )
}

// Use module.exports to be compatible with Draft js packing
module.exports = insertFragmentIntoContentState
